import React from 'react';
import { func, bool, string } from 'prop-types';
import {
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import MobilePhoneField from 'Elements/MobilePhoneField';
import { withAnalyticsHOC } from 'Scenes/_root/analyticsContext';
import getHelperTextFromErrors from 'Services/helpers/errorText';
import validate from 'Services/validate';
import styles from './styles.scss';

const mobileValidation = {
  required: true,
  validMobileNumber: true,
};

const emailValidation = {
  required: true,
  validEmailAddress: true,
};

export class InviteForm extends React.Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.isFetchingPatientContactDetails !== prevState.isFetchingPatientContactDetails) {
      let inviteTransmissionMethod = 'sms';
      const mobileErrors = validate(nextProps.incomingPatientMobile, mobileValidation);
      const emailErrors = validate(nextProps.incomingPatientEmail, emailValidation);
      if (nextProps.incomingPatientMobile) {
        if (
          validate(nextProps.incomingPatientMobile, mobileValidation)
          && nextProps.incomingPatientEmail
          && !validate(nextProps.incomingPatientEmail, emailValidation)) {
          inviteTransmissionMethod = 'email';
        }
      } else if (nextProps.incomingPatientEmail) {
        inviteTransmissionMethod = 'email';
      }
      return {
        isFetchingPatientContactDetails: nextProps.isFetchingPatientContactDetails,
        email: nextProps.incomingPatientEmail,
        emailTouched: prevState.email !== nextProps.incomingPatientEmail,
        emailErrors,
        mobile: mobileErrors ? prevState.mobile : nextProps.incomingPatientMobile,
        mobileTouched: !mobileErrors,
        mobileErrors,
        inviteTransmissionMethod,
      };
    }
    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      isFetchingPatientContactDetails: props.isFetchingPatientContactDetails,
      mobile: props.incomingPatientMobile || '',
      mobileErrors: validate('', mobileValidation),
      mobileTouched: false,
      email: props.incomingPatientEmail || '',
      emailErrors: validate('', emailValidation),
      emailTouched: false,
      inviteTransmissionMethod: 'sms',
    };
  }

  handleFormSubmit = (event) => {
    const {
      mobile,
      mobileErrors,
      email,
      emailErrors,
      inviteTransmissionMethod,
    } = this.state;
    const {
      meetingCreate,
      trackEvent,
    } = this.props;
    event.preventDefault();
    // create meeting
    if (inviteTransmissionMethod === 'sms') {
      if (mobileErrors) return;
      const payLoadKey = 'patientMobile';
      meetingCreate(mobile.replace(/ /g, ''), payLoadKey)
        .then(() => {
          trackEvent('sms invite send success');
        })
        .catch(() => {
          trackEvent('sms invite send failure');
        });
    } else {
      if (emailErrors) return;
      const payLoadKey = 'patientEmail';
      meetingCreate(email, payLoadKey)
        .then(() => {
          trackEvent('email invite send success');
        })
        .catch(() => {
          trackEvent('email invite send failure');
        });
    }
  }

  handleMobileChange = (event) => {
    this.setState({
      mobile: event.target.value,
      mobileErrors: validate(event.target.value, mobileValidation),
      mobileTouched: true,
    });
  }

  handleMobileBlur = () => {
    this.setState({
      mobileTouched: true,
    });
  }

  handleEmailChange = (event) => {
    this.setState({
      email: event.target.value,
      emailErrors: validate(event.target.value, emailValidation),
      emailTouched: true,
    });
  }

  handleEmailBlur = () => {
    this.setState({
      emailTouched: true,
    });
  }

  handleInviteMethodSelect = (event) => {
    this.setState({
      inviteTransmissionMethod: event.target.value,
    });
  }

  isSubmitDisabled = () => {
    const {
      inviteTransmissionMethod,
      mobileErrors,
      emailErrors,
    } = this.state;
    const {
      isCreatingMeeting,
    } = this.props;
    return (
      isCreatingMeeting
      || (inviteTransmissionMethod === 'sms' && !!mobileErrors)
      || (inviteTransmissionMethod === 'email' && !!emailErrors)
    );
  }


  render() {
    const {
      mobile,
      mobileErrors,
      mobileTouched,
      email,
      emailErrors,
      emailTouched,
      inviteTransmissionMethod,
    } = this.state;
    const {
      isFetchingPatientContactDetails,
      isCreatingMeeting,
      incomingPatientShowError,
    } = this.props;
    return (
      <React.Fragment>
        <div className={styles.inviteHeading}>
        { incomingPatientShowError &&
          <Alert severity="warning">Unable to retrieve a valid email address or contact number.
          Please check the patient record or enter it below to start the teleconsult.</Alert>
        }
          <Typography variant='h2'>Invite a patient</Typography>
          <TextField
            key='inviteTransmissionMethod'
            fullWidth
            select
            id='inviteTransmissionMethod-select'
            label='Invitation method'
            margin='dense'
            onChange={this.handleInviteMethodSelect}
            size='small'
            value={inviteTransmissionMethod}
            variant='outlined'
          >
            <MenuItem value={'sms'}>SMS</MenuItem>
            <MenuItem value={'email'}>Email</MenuItem>
          </TextField>
        </div>
        <form className={styles.inviteForm} onSubmit={this.handleFormSubmit}>
        { inviteTransmissionMethod === 'sms' &&
          <React.Fragment>
            <Typography variant='body1'>
              Please enter a patient’s mobile number below to send them an SMS invitation
            </Typography>
            <TextField
              fullWidth
              required
              autoComplete='off'
              disabled={isFetchingPatientContactDetails}
              error={mobileTouched && !!mobileErrors}
              helperText={mobileTouched && getHelperTextFromErrors(mobileErrors)}
              InputProps={{
                inputComponent: MobilePhoneField,
              }}
              margin='dense'
              name='mobile'
              onBlur={this.handleMobileBlur}
              onChange={this.handleMobileChange}
              placeholder='Enter patient’s mobile number'
              size='small'
              value={mobile}
              variant='outlined'
            />
          </React.Fragment>
        }
        { inviteTransmissionMethod === 'email' &&
          <React.Fragment>
            <Typography variant='body1'>
              Please enter a patient’s email address below to send them an email invitation
            </Typography>
            <TextField
              fullWidth
              required
              autoComplete='off'
              disabled={isFetchingPatientContactDetails}
              error={emailTouched && !!emailErrors}
              helperText={emailTouched && getHelperTextFromErrors(emailErrors)}
              InputProps={{ type: 'email ' }}
              margin='dense'
              name='email'
              onBlur={this.handleEmailBlur}
              onChange={this.handleEmailChange}
              placeholder='Enter patient’s email address'
              size='small'
              value={email}
              variant='outlined'
            />
          </React.Fragment>
        }
          <div className={styles.buttonWrapper}>
            <Button
              fullWidth
              color='primary'
              disabled={this.isSubmitDisabled()}
              type='submit'
              variant='contained'
            >
              Send invitation
            </Button>
            { isCreatingMeeting && <CircularProgress className={styles.buttonProgress} size={24} /> }
          </div>
        </form>
      </React.Fragment>
    );
  }
}

const MapStateProps = {
  meetingPutPatientDetails: func.isRequired,
  isFetchingPatientContactDetails: bool.isRequired,
  incomingPatientEmail: string,
  incomingPatientMobile: string,
  incomingPatientShowError: bool,
  isCreatingMeeting: bool.isRequired,
};

const MapDispatchProps = {
  meetingCreate: func.isRequired,
};

InviteForm.propTypes = {
  ...MapStateProps,
  ...MapDispatchProps,
};

export default withAnalyticsHOC()(InviteForm);
